import { type ReactNode, useId } from 'react';

import cn from 'classnames';

import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './IconBadge.module.scss';

type Size = 'sm' | 'md' | 'lg' | 'xl';

export type BadgeColors = 'navy' | 'white' | 'blue' | 'teal' | 'green' | 'purple' | 'lime' | 'orange' | 'red';

type GradientStop = { offset: string; color: string };

type Gradient = { stops: GradientStop[]; angle?: number };

const colorMap: Record<BadgeColors, string> = {
  navy: '#042354',
  white: '#fff',
  blue: '#0550e2',
  teal: '#0891b2',
  green: '#16a34a',
  purple: '#7c3aed',
  lime: '#8eda41',
  orange: '#fab247',
  red: '#e42f2f',
};

const BLOB_PATH =
  'M8.03322 7.80228C1.70301 14.27 -0.507535 29.8403 0.0953414 41.5779C0.430273 48.1483 1.93747 59.9886 8.23418 65.1559C13.794 69.7757 24.9807 72 36 72C47.0192 72 58.206 69.7072 63.7658 65.1559C70.0625 59.9886 71.5697 48.0456 71.9047 41.5779C72.5075 29.8403 70.297 14.2015 63.9668 7.80228C53.7849 -2.60076 18.2151 -2.60076 8.03322 7.80228Z';

type Props = {
  iconType?: string;
  children?: ReactNode;
  color?: BadgeColors;
  gradient?: Gradient;
  size?: Size;
  border?: boolean;
  className?: string;
};

const iconSizes: Record<Size, number> = {
  sm: 20,
  md: 38,
  lg: 50,
  xl: 66,
};

const anglToSvgCoords = (angle: number) => {
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    x1: `${(0.5 - Math.cos(rad) * 0.5) * 100}%`,
    y1: `${(0.5 - Math.sin(rad) * 0.5) * 100}%`,
    x2: `${(0.5 + Math.cos(rad) * 0.5) * 100}%`,
    y2: `${(0.5 + Math.sin(rad) * 0.5) * 100}%`,
  };
};

export const IconBadge = ({
  iconType,
  children,
  color = 'navy',
  gradient,
  size = 'md',
  border = false,
  className,
}: Props) => {
  const gradientId = useId();
  const fill = gradient ? `url(#${gradientId})` : colorMap[color];
  const coords = gradient ? anglToSvgCoords(gradient.angle ?? 135) : null;
  const borderHighlightCoords = border ? anglToSvgCoords(305.04) : null;
  const borderHighlightId = `${gradientId}-border`;

  return (
    <div className={cn(styles.iconBadge, styles[`iconBadge_${size}`], className)}>
      <svg
        className={cn(styles.ellipse, border && styles.ellipse_bordered)}
        viewBox="0 0 72 72"
        xmlns="http://www.w3.org/2000/svg"
        overflow="visible"
        fill="none"
        aria-hidden="true"
      >
        <defs>
          {gradient && coords && (
            <linearGradient
              id={gradientId}
              x1={coords.x1}
              y1={coords.y1}
              x2={coords.x2}
              y2={coords.y2}
              gradientUnits="objectBoundingBox"
            >
              {gradient.stops.map((stop) => (
                <stop key={stop.offset} offset={stop.offset} stopColor={stop.color} />
              ))}
            </linearGradient>
          )}

          {border && borderHighlightCoords && (
            <linearGradient
              id={borderHighlightId}
              x1={borderHighlightCoords.x1}
              y1={borderHighlightCoords.y1}
              x2={borderHighlightCoords.x2}
              y2={borderHighlightCoords.y2}
              gradientUnits="objectBoundingBox"
            >
              <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
              <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
            </linearGradient>
          )}
        </defs>

        <path
          d={BLOB_PATH}
          fill={fill}
          stroke={border ? '#DFE4EC' : 'none'}
          strokeWidth={border ? 1.5 : 0}
          paintOrder="stroke fill"
        />

        {border && <path d={BLOB_PATH} fill="none" stroke={`url(#${borderHighlightId})`} strokeWidth={1.5} />}
      </svg>

      <span className={styles.icon}>
        {children ?? (iconType && <Icon type={iconType} style={{ fontSize: `${iconSizes[size]}px` }} />)}
      </span>
    </div>
  );
};
